import type {
  LoginAuthenticationResponse,
  LoginProfileResponse,
  LoginScopeResponse,
  MedplumClient,
} from "@medplum/core";
import type { ProjectMembership } from "@medplum/fhirtypes";
import { REDIRECT_PROBE_ORIGIN } from "@/lib/auth/safe-redirect-path";
import { isNonEmptyString } from "@/lib/auth/string-guards";

export type LoginChallengeResult =
  | {
      status: "requires-mfa";
    }
  | {
      status: "complete";
      code: string;
    }
  | {
      status: "select-profile";
      loginId: string;
      memberships: ProjectMembership[];
      membershipFilterToken?: string;
    }
  | {
      status: "error";
    };

interface LoginCompletionResult {
  readonly profile: Awaited<ReturnType<MedplumClient["processCode"]>>;
  readonly activeLogin: NonNullable<ReturnType<MedplumClient["getActiveLogin"]>>;
}

interface ProfileSelectionResponse extends Partial<LoginProfileResponse> {
  code?: string;
}

/**
 * Resolves the next login step from Medplum's authentication response.
 */
export function resolveLoginChallenge({
  response,
}: {
  response: LoginAuthenticationResponse;
}): LoginChallengeResult {
  if (response.mfaRequired || response.mfaEnrollRequired) {
    return { status: "requires-mfa" };
  }

  if (isNonEmptyString(response.code)) {
    return {
      status: "complete",
      code: response.code,
    };
  }

  const activeMemberships = response.memberships?.filter(isLoginSelectableMembership) ?? [];
  if (activeMemberships.length) {
    if (!isNonEmptyString(response.login)) {
      return { status: "error" };
    }

    const membershipFilterToken = getMembershipFilterToken({ response });
    return {
      status: "select-profile",
      loginId: response.login,
      memberships: activeMemberships,
      ...(membershipFilterToken ? { membershipFilterToken } : {}),
    };
  }

  return { status: "error" };
}

/**
 * Treats memberships with active=false as hidden from login profile selection.
 */
function isLoginSelectableMembership(membership: ProjectMembership): boolean {
  return membership.active !== false;
}

function getMembershipFilterToken({
  response,
}: {
  response: LoginAuthenticationResponse;
}): string | undefined {
  const rawToken = (response as LoginAuthenticationResponse & { membershipFilterToken?: unknown })
    .membershipFilterToken;
  return typeof rawToken === "string" && rawToken.trim().length > 0 ? rawToken : undefined;
}

/**
 * Processes an auth code and ensures an active login is available.
 */
export async function completeLoginFromCode({
  medplum,
  code,
}: {
  medplum: MedplumClient;
  code: string;
}): Promise<LoginCompletionResult> {
  const profile = await medplum.processCode(code);
  const activeLogin = medplum.getActiveLogin();

  if (!activeLogin) {
    throw new Error("Unable to establish a login session.");
  }

  return { profile, activeLogin };
}

/**
 * Selects a profile and resolves the final authorization code.
 *
 * Keep behavior aligned with apps/mobile/lib/auth/login-flow.ts.
 */
export async function resolveLoginCodeFromProfileSelection({
  medplum,
  loginId,
  membershipId,
}: {
  medplum: MedplumClient;
  loginId: string;
  membershipId: string;
}): Promise<string> {
  if (!isNonEmptyString(loginId)) {
    throw new Error("Missing loginId");
  }

  if (!isNonEmptyString(membershipId)) {
    throw new Error("Missing membershipId");
  }

  const profileSelection = (await medplum.post("auth/profile", {
    login: loginId,
    profile: membershipId,
  })) as ProfileSelectionResponse;

  if (isNonEmptyString(profileSelection.code)) {
    return profileSelection.code;
  }

  if (!isNonEmptyString(profileSelection.scope)) {
    throw new Error("Login failed. Please try again.");
  }

  const scopeSelection = (await medplum.post("auth/scope", {
    login: profileSelection.login ?? loginId,
    scope: profileSelection.scope,
  })) as Partial<LoginScopeResponse>;

  if (!isNonEmptyString(scopeSelection.code)) {
    throw new Error("Login failed. Please try again.");
  }

  return scopeSelection.code;
}

/**
 * Returns a safe internal path for client-side redirects.
 */
export function getSafeRedirectPath({
  nextPath,
  fallbackPath = "/",
}: {
  nextPath?: string | null;
  fallbackPath?: string;
}): string {
  if (!nextPath) {
    return fallbackPath;
  }

  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(nextPath).trim();
  } catch {
    return fallbackPath;
  }

  // Resolve the path against a placeholder origin and keep it only if the origin
  // does not move. The URL parser also removes tab, CR, and LF, so an interior
  // control character cannot turn "/\n/evil.com" into "//evil.com".
  let probe: URL;
  try {
    probe = new URL(decodedPath, REDIRECT_PROBE_ORIGIN);
  } catch {
    return fallbackPath;
  }

  if (probe.origin !== REDIRECT_PROBE_ORIGIN) {
    return fallbackPath;
  }

  // The origin check alone is not enough. The parser normalizes "/.//evil.com" and
  // "/..//evil.com" to the pathname "//evil.com" on the placeholder origin. That string
  // is protocol-relative, so `router.push` would send it to https://evil.com/.
  if (!probe.pathname.startsWith("/") || probe.pathname.startsWith("//")) {
    return fallbackPath;
  }

  return probe.pathname + probe.search + probe.hash;
}
